<template>
  <section class="inquiry-table-section">
    <b-field class="bulk-actions">
      <b-button
        icon-left="delete"
        type="is-danger"
        v-if="showDeleteButton() && !isForAdmin && isManager"
        @click="handleBulkRemove"
      >
        Remove
        <span class="bulk-action-text">selected inquiries</span>
      </b-button>
      <b-button
        class="ml-3"
        icon-left="archive"
        type="is-warning"
        v-if="showArchivedButton() && !isForAdmin && canArchiveInquiries"
        @click="handleBulkArchive"
      >
        Archive
        <span class="bulk-action-text">selected inquiries</span>
      </b-button>
      <b-button
        class="ml-3"
        icon-left="archive"
        type="is-success"
        v-if="showUnarchivedButton() && !isForAdmin && canArchiveInquiries"
        @click="handleBulkUnarchive"
      >
        Unarchive
        <span class="bulk-action-text">selected inquiries</span>
      </b-button>
      <b-button
        class="ml-3"
        icon-left="refresh"
        type="is-info"
        v-if="!isForAdmin && selectedCount > 1 && permBatchInquiry"
        @click="handleRunBatchInquiry"
      >
        Run as Batch Report
        <span class="bulk-action-text">({{ selectedCount }} selected)</span>
      </b-button>
    </b-field>

    <b-field class="search-section">
      <div class="field-body">
        <b-input
          class="is-expanded"
          :loading="loading"
          v-model="search"
          placeholder="Search by First or last name, DOB (MM/DD/YYYY), Member ID, or Payer, Group Name"
          @input="emitSearchTerm"
        />
        <div class="archived-toggle-wrapper">
          <b-tooltip
            label="Enable archived to see the archived inquiries"
            position="is-top"
            type="is-dark"
            :active="!archivedToggle"
          >
            <div class="archived-btn" :class="{ 'is-active': archivedToggle }">
              <b-switch
                @input="handleArchiveToggle($event)"
                :value="archivedToggle"
                type="is-success"
              ></b-switch>
              <span class="archived-btn-text">Archived</span>
              <span v-if="archivedCount > 0" class="archived-count">
                {{ archivedCount }}
              </span>
            </div>
          </b-tooltip>
        </div>
        <b-dropdown
          class="filter-dropdown"
          position="is-bottom-left"
          v-model="searchableFields"
          multiple
          aria-role="list"
        >
          <template #trigger>
            <b-button icon-right="filter" type="is-primary" class="filter-btn">
              <span class="filter-btn-text">
                Search Fields
                <span class="filter-count">({{ searchableFields.length }})</span>
              </span>
            </b-button>
          </template>

          <b-dropdown-item
            v-for="field in allFields"
            :key="field.value"
            :value="field.value"
            aria-role="listitem"
          >
            <div @click="toggleCheckbox(field.value)">
              <b-checkbox
                v-model="searchableFields"
                :native-value="field.value"
                class="checkbox-left"
              >
                {{ field.label }}
              </b-checkbox>
            </div>
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </b-field>

    <b-table
      :data="options"
      :loading="loading"
      backend-sorting
      custom-row-key="id"
      sort-icon="chevron-up"
      sort-icon-size="is-small"
      :default-sort="['created_at', 'desc']"
      default-sort-direction="desc"
      striped
      hoverable
      :checkable="!isForAdmin && canSelectInquiries"
      :checked-rows.sync="checkedRows"
      :debounce-search="1000"
      :detailed="isForAdmin"
      detail-key="id"
      detail-transition="fade"
      :show-detail-icon="showDetailIcon"
      @sort="handleSort"
    >

      <b-table-column field="patient" label="Patient" sortable v-slot="props">
        <div>
          <b-tooltip
            v-if="props.row.alerts && props.row.alerts.length > 0"
            :label="props.row.alerts[0].message"
            position="is-right"
          >
            <b-icon
              icon="bell"
              size="is-small"
              :style="{ color: props.row.alerts[0].color }"
            />
          </b-tooltip>
        
        <router-link
          v-if="props.row.status === 'complete' || props.row.status === 'expired'"
          :to="{ name: 'InquiryDetail', params: { id: props.row.id } }"
        >
          {{ props.row.patient && props.row.patient.trim() ? props.row.patient : 'UNKNOWN' }}
        </router-link>
        <span v-else>
          {{ props.row.patient && props.row.patient.trim() ? props.row.patient : 'UNKNOWN' }}
        </span>
        </div>
          
      </b-table-column>

      <b-table-column field="user" label="User" v-slot="props">
        {{ props.row.user }}
      </b-table-column>

      <b-table-column field="organization" label="Organization" v-slot="props" v-if="isForAdmin">
        {{ props.row.organization || '-' }}
      </b-table-column>

      <b-table-column field="provider" label="Provider" v-slot="props" v-if="isForAdmin">
        {{ props.row.provider }}
      </b-table-column>

      <b-table-column field="payer" label="Payer" v-slot="props">
        {{ props.row.payer }}
      </b-table-column>

      <b-table-column field="created_at" label="Date" sortable centered v-slot="props">
        {{ props.row.date }}
      </b-table-column>

      <b-table-column v-if="isForAdmin" field="is_hidden" label="Visibility" numeric v-slot="props">
        <b-icon v-if="props.row.is_hidden" icon="eye-off-outline"></b-icon>
      </b-table-column>

      <b-table-column field="status" label="Status" numeric v-slot="props">
        <div class="is-flex is-align-items-center is-justify-content-flex-end">
          <!-- Icon slot (fixed width) - only show container when viewing archived -->
          <div class="archive-icon-container" v-if="archivedToggle">
            <b-tooltip
              v-if="props.row.is_archived"
              label="Archived"
              position="is-left"
              type="is-dark"
            >
              <b-icon icon="archive" class="has-text-grey-dark archive-status-icon" />
            </b-tooltip>
          </div>
          <span
            :class="{
              tag: true,
              'is-success': props.row.plan_status == 'active',
              'is-danger': props.row.plan_status == 'inactive',
              'is-light': props.row.plan_status == 'draft',
              'is-warning': props.row.plan_status == 'unavailable',
            }"
            style="display: inline-flex; align-items: center; line-height: 1"
          >
            <span>{{ props.row.plan_status }}</span>
          </span>
        </div>
      </b-table-column>
      <b-table-column v-if="isForAdmin" field="had_reimbursement" label="EIV" v-slot="props">
        <b-icon
          v-if="props.row.had_reimbursement"
          type="is-success"
          icon="check-circle"
          size="is-small"
        />
      </b-table-column>

      <b-table-column field="source" v-if="isForAdmin" label="Source" v-slot="props">
        <div class="is-flex">
          <b-tooltip
            v-if="props.row.source === 'version1'"
            label="version1"
            position="is-left"
            type="is-primary"
          >
            <b-icon icon="alpha-a-circle" class="mr-1" type="is-primary" />
          </b-tooltip>
          <b-tooltip
            v-if="props.row.source === 'version2'"
            label="version2"
            position="is-left"
            type="is-info"
          >
            <b-icon icon="alpha-p-circle" class="mr-1" type="is-info" />
          </b-tooltip>
        </div>
        <router-link
          :to="{ name: 'AdminInquiryDetail', params: { id: props.row.id } }"
          v-if="isForAdmin"
        >
          <b-icon icon="eye" class="mr-2" type="is-dark"></b-icon>
        </router-link>
      </b-table-column>

      <b-table-column label="Actions" numeric v-slot="props" v-if="!isForAdmin">
        <div class="action-buttons">
          <router-link
            :to="{ name: 'InquiryDraft', params: { id: props.row.id } }"
            v-if="props.row.status === 'draft'"
          >
            <b-icon icon="book-edit"></b-icon>
          </router-link>
          <router-link :to="{ name: 'InquiryCopy', params: { id: props.row.id } }" v-else>
            <b-icon icon="reload"></b-icon>
          </router-link>
          <!-- Mobile action icons -->
          <span class="mobile-action-icons" v-if="isManager || canArchiveInquiries">
            <b-tooltip v-if="isManager" label="Delete" position="is-left" type="is-dark">
              <b-icon
                icon="delete"
                class="has-text-danger is-clickable"
                @click.native="$emit('single-delete', props.row)"
              ></b-icon>
            </b-tooltip>
            <b-tooltip
              v-if="canArchiveInquiries"
              :label="props.row.is_archived ? 'Unarchive' : 'Archive'"
              position="is-left"
              type="is-dark"
            >
              <b-icon
                :icon="props.row.is_archived ? 'archive-arrow-up' : 'archive'"
                :class="
                  props.row.is_archived
                    ? 'has-text-success is-clickable'
                    : 'has-text-warning is-clickable'
                "
                @click.native="$emit('single-archive', props.row)"
              ></b-icon>
            </b-tooltip>
          </span>
        </div>
      </b-table-column>
      <template #detail="props">
        <p>
          <strong>ID:</strong>
          {{ props.row.id }}
          <br />
          <strong>Patient:</strong>
          {{ props.row.patient }}
          <br />
          <strong>User:</strong>
          {{ props.row.user }}
          <br />
          <template v-if="isForAdmin">
            <strong>Organization:</strong>
            {{ props.row.organization || '-' }}
            <br />
            <strong>Provider:</strong>
            {{ props.row.provider }}
            <br />
          </template>
          <strong>Payer:</strong>
          {{ props.row.payer }}
          <br />
          <strong>Date:</strong>
          {{ props.row.date }}
          <br />
          <strong>Status:</strong>
          {{ props.row.status }}
          <br />
          <strong>Plan Status:</strong>
          {{ props.row.plan_status }}
          <br />
          <template v-if="isForAdmin">
            <strong>Had Reimbursement:</strong>
            {{ props.row.had_reimbursement || 'N/A' }}
            <br />
          </template>

          <strong>Member ID:</strong>
          {{ props.row.submission.memberId }}
          <br />
          <strong>First Name:</strong>
          {{ props.row.submission.patientFirstName }}
          <br />
          <strong>Last Name:</strong>
          {{ props.row.submission.patientLastName }}
          <br />
          <strong>Birth Date:</strong>
          {{ props.row.submission.patientBirthDate }}
          <br />
          <strong>Gender:</strong>
          {{ props.row.submission.patientGender || 'N/A' }}
          <br />
          <strong>Group Number:</strong>
          {{ props.row.submission.groupNumber || 'N/A' }}
          <br />
          <strong>Subscriber Relationship:</strong>
          {{ props.row.submission.subscriberRelationship }}
          <br />
          <strong>Service Types:</strong>
          {{ props.row.submission.serviceType.join(', ') }}
          <br />
          <template v-if="isForAdmin">
            <strong>Source:</strong>
            {{ props.row.source }}
          </template>
        </p>
      </template>

      <template #empty>
        <!-- Loading State -->
        <div v-if="loading" class="loader-container">
          <div class="loader"></div>
          <p class="mt-3 has-text-grey">Loading inquiries...</p>
        </div>

        <!-- True Empty State -->
        <div v-else class="has-text-centered py-6">
          <b-icon icon="folder-open" size="is-large" type="is-grey-light"></b-icon>

          <p class="has-text-weight-bold mt-3">No inquiries found</p>
          <p class="has-text-grey">
            There are no inquiries matching your current filters.
          </p>

          <b-message type="is-warning" class="mt-4 is-inline-block" v-if="archivedCount > 0">
            <b-icon icon="lightbulb-on-outline" size="is-small" class="mr-2"></b-icon>
            Looking for something? Try checking the
            <strong style="color: black">Archived</strong>
            toggle above or change your search fields.
          </b-message>
        </div>
      </template>
    </b-table>

    <div class="pagination-container" v-if="totalItems && pageSize">
      <b-pagination
        v-model="currentPageLocal"
        :total="totalItems"
        :per-page="pageSize"
        @change="emitPageChange"
      ></b-pagination>
    </div>
  </section>
</template>

<script>
import request from '../request';
import { mapGetters } from 'vuex';
import { sanitizeSearchInput } from '../utils/form';
export default {
  name: 'InquiryTable',
  props: {
    options: Array,
    archivedCount: {
      type: Number,
      default: 0,
    },
    isForAdmin: {
      type: Boolean,
      default: false,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    pageSize: {
      type: Number,
      required: false,
    },
    totalItems: {
      type: Number,
      required: false,
    },
    loading: {
      type: Boolean,
      default: true,
    },
    // Any string the parent recomputes when its own filters (user, provider,
    // status) change. Changing it drops the persistent selection; paging does
    // not change it, so a selection can still span pages.
    filterKey: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      search: '',
      searchableFields: [
        'patient_first_name',
        'patient_last_name',
        'member_id',
        'patient_dob',
        'payer_name',
        'group_name',
      ],
      allFields: [
        { value: 'patient_first_name', label: 'First Name' },
        { value: 'patient_last_name', label: 'Last Name' },
        { value: 'patient_dob', label: 'Birth Date' },
        { value: 'member_id', label: 'Member ID' },
        { value: 'payer_name', label: 'Payer Name' },
        { value: 'group_name', label: 'Group Name' },
      ],
      checkedRows: [],
      // Persistent cross-page selection (id -> row), so a batch can span
      // multiple pages even though Buefy's checked-rows is per-page.
      selectedMap: {},
      restoringChecked: false,
      showDetailIcon: true,
      currentPageLocal: this.currentPage,
      archivedToggle: false,
      deletingInquiries: false,
      archivingInquiries: false,
    };
  },
  computed: {
    ...mapGetters(['isManager', 'isRestricted', 'permBatchInquiry']),
    canSelectInquiries() {
      return this.isManager || !this.isRestricted || this.permBatchInquiry;
    },
    canArchiveInquiries() {
      return !this.isRestricted;
    },
    selectedIds() {
      return Object.keys(this.selectedMap);
    },
    selectedCount() {
      return this.selectedIds.length;
    },
    // The rows behind `selectedMap`, so bulk actions and the buttons that show
    // them can look at the whole selection and not just the visible page.
    selectedRows() {
      return Object.values(this.selectedMap);
    },
  },
  watch: {
    // Merge the current page's checked rows into the persistent selection.
    checkedRows(rows) {
      if (this.restoringChecked) return;
      const visibleIds = (this.options || []).map((o) => String(o.id));
      const checkedIds = new Set(rows.map((r) => String(r.id)));
      const map = { ...this.selectedMap };
      visibleIds.forEach((id) => {
        if (checkedIds.has(id)) {
          const row = rows.find((r) => String(r.id) === id);
          if (row) map[id] = row;
        } else {
          delete map[id];
        }
      });
      this.selectedMap = map;
    },
    // A filter change means the result set is no longer the one the selection
    // was made against, so start over instead of acting on hidden rows.
    filterKey() {
      this.clearSelection();
    },
    // The parent resets to page 1 when the sort or search changes, so mirror it
    // here or the pagination widget keeps highlighting the previous page.
    currentPage(page) {
      this.currentPageLocal = page;
    },
    // When the page changes, restore the checkmarks for already-selected rows.
    options() {
      this.restoringChecked = true;
      this.checkedRows = (this.options || []).filter((o) => this.selectedMap[String(o.id)]);
      this.$nextTick(() => {
        this.restoringChecked = false;
      });
    },
  },
  beforeDestroy() {
    clearTimeout(this._searchDebounceTimer);
  },
  methods: {
    handleRunBatchInquiry() {
      // Keep a defensive permission check in addition to hiding the button.
      if (!this.permBatchInquiry) {
        this.$buefy.dialog.alert({
          title: 'Batch Reports not enabled',
          message:
            'Running Batch Reports isn’t enabled for your account. ' +
            'Please ask your manager or admin to enable the Batch Reports feature for you.',
          type: 'is-warning',
          hasIcon: true,
          icon: 'account-lock',
        });
        return;
      }

      const ids = this.selectedIds;
      if (ids.length < 2) return;

      // Archived inquiries can't be re-run as a batch.
      const archived = ids.filter((id) => this.selectedMap[id] && this.selectedMap[id].is_archived);
      if (archived.length > 0) {
        this.$buefy.dialog.alert({
          title: 'Archived inquiries can’t be re-run',
          message:
            `${archived.length} of the selected inquiries are archived and can’t be included in a ` +
            'Batch Report. Please deselect them, or unarchive them first.',
          type: 'is-warning',
          hasIcon: true,
          icon: 'archive-alert',
        });
        return;
      }

      // Too many ids for a query string, so stash them for BatchInquiryCreate. Keep a
      // light copy of each row so BatchInquiryCreate can list/filter by patient name.
      const rows = ids.map((id) => {
        const row = this.selectedMap[id] || {};
        return {
          id: String(id),
          patient: row.patient || '',
          payer_name: row.payer || '',
        };
      });
      sessionStorage.setItem('batchInquiryIds', JSON.stringify(ids));
      sessionStorage.setItem('batchInquiryRows', JSON.stringify(rows));
      this.$router.push({ name: 'BatchInquiryCreate' });
    },
    handleBulkRemove() {
      const count = this.selectedCount;
      let message = `Are you sure you want to delete ${count} inquiries along with all their history?`;
      message += `<br/><br/>NOTE: This will not delete the related data, but these inquiries will no longer be visible to you or your organization's users.`;

      this.$buefy.dialog.confirm({
        title: 'Remove inquiries',
        message: message,
        confirmText: `Remove ${count} inquiries`,
        type: 'is-danger',
        onConfirm: () => {
          this.deletingInquiries = true;
          let ids = this.selectedIds;

          const loadingComponent = this.$buefy.loading.open({
            container: this.$el,
          });

          request
            .put(`/inquiries`, { inquiry_ids: ids, is_hidden: true, is_grouped: true })
            .then(() => {
              this.$buefy.toast.open({
                message: `Successfully removed ${count} inquiries`,
                type: 'is-success',
                duration: 10000,
              });
              this.clearSelection();
            })
            .catch((error) => {
              this.$buefy.toast.open({
                message: error.response?.data?.error || 'Failed to remove inquiries',
                type: 'is-danger',
                duration: 5000,
              });
            })
            .finally(() => {
              this.deletingInquiries = false;
              loadingComponent.close();
              // emit regardless just to ensure we are up to date
              this.$emit('bulkremove');
            });
        },
      });
    },
    handleBulkArchive() {
      const count = this.selectedCount;
      let message = `Are you sure you want to archive ${count} inquiries along with all their history?`;
      this.$buefy.dialog.confirm({
        title: 'Archive inquiries',
        message: message,
        confirmText: `Archive ${count} inquiries`,
        type: 'is-warning',
        onConfirm: () => {
          this.archivingInquiries = true;
          let ids = this.selectedIds;

          const loadingComponent = this.$buefy.loading.open({
            container: this.$el,
          });

          request
            .put(`/inquiries/archive`, { inquiry_ids: ids, is_archived: true, is_grouped: true })
            .then(() => {
              this.$buefy.toast.open({
                message: `Successfully archived ${count} inquiries`,
                type: 'is-success',
                duration: 10000,
              });
              this.clearSelection();
            })
            .catch((error) => {
              this.$buefy.toast.open({
                message: error.response?.data?.error || 'Failed to archive inquiries',
                type: 'is-danger',
                duration: 5000,
              });
            })
            .finally(() => {
              this.archivingInquiries = false;
              loadingComponent.close();
              this.$emit('bulkarchive');
            });
        },
      });
    },
    handleBulkUnarchive() {
      const count = this.selectedCount;
      let message = `Are you sure you want to Unarchive ${count} inquiries along with all their history?`;
      this.$buefy.dialog.confirm({
        title: 'Unarchive inquiries',
        message: message,
        confirmText: `Unarchive ${count} inquiries`,
        type: 'is-success',
        onConfirm: () => {
          this.archivingInquiries = true;
          let ids = this.selectedIds;

          const loadingComponent = this.$buefy.loading.open({
            container: this.$el,
          });

          request
            .put(`/inquiries/archive`, { inquiry_ids: ids, is_archived: false, is_grouped: true })
            .then(() => {
              this.$buefy.toast.open({
                message: `Successfully Unarchived ${count} inquiries`,
                type: 'is-success',
                duration: 10000,
              });
              this.clearSelection();
            })
            .catch((error) => {
              this.$buefy.toast.open({
                message: error.response?.data?.error || 'Failed to unarchive inquiries',
                type: 'is-danger',
                duration: 5000,
              });
            })
            .finally(() => {
              this.archivingInquiries = false;
              loadingComponent.close();
              this.$emit('bulkarchive');
            });
        },
      });
    },

    emitPageChange(newPage) {
      this.$emit('page-change', newPage); // Emit event to parent component when page changes
    },
    // Drop the persistent selection. Called whenever the result set changes for
    // a reason other than paging, so bulk actions can never run on rows the
    // user can no longer see.
    clearSelection() {
      this.checkedRows = [];
      this.selectedMap = {};
    },
    // The table only shows one page of `options` at a time, so Buefy's built-in
    // sort would only reorder the current page. `backend-sorting` on the table
    // turns that off, and we ask the parent to re-fetch page 1 ordered by the
    // backend instead. Columns the backend can't order by yet have had
    // `sortable` removed rather than being left to silently do nothing.
    handleSort(field, order) {
      this.$emit('sort-changed', { field, order });
    },
    emitSearchTerm() {
      this.clearSelection();
      clearTimeout(this._searchDebounceTimer);
      this._searchDebounceTimer = setTimeout(() => {
        const search = sanitizeSearchInput(this.search);
        const fields = this.isValidPatientDOB(search)
          ? this.searchableFields
          : this.searchableFields.filter((field) => field !== 'patient_dob');

        this.$emit('search-changed', {
          search,
          fields,
        });
      }, 600);
    },
    isValidPatientDOB(value) {
      const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
      if (!match) return false;

      const month = Number(match[1]);
      const day = Number(match[2]);
      const year = Number(match[3]);
      const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
      const daysInMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

      return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth[month - 1];
    },
    handleArchiveToggle(event) {
      this.clearSelection();
      this.archivedToggle = event;
      this.$emit('archive-toggle-change', {
        event: event,
      });
    },
    toggleCheckbox(value) {
      const index = this.searchableFields.indexOf(value);
      if (index === -1) {
        this.searchableFields.push(value); // Add the field to the selected list
      } else {
        this.searchableFields.splice(index, 1); // Remove the field from the selected list
      }
      this.emitSearchTerm();
    },
    showDeleteButton() {
      return this.selectedCount > 0;
    },
    showArchivedButton() {
      const hasUnArchived = this.selectedRows.some((row) => row.is_archived === false);
      return hasUnArchived;
    },
    showUnarchivedButton() {
      const hasArchived = this.selectedRows.some((row) => row.is_archived === true);
      return hasArchived;
    },
  },
};
</script>

<style scoped>
.pagination-container {
  display: flex;
  justify-content: flex-end;
}

/* Hold the table's height steady while a sort/page re-fetch is in flight, so
   the rows below it don't jump as the new page swaps in. */
.inquiry-table-section >>> .table-wrapper {
  min-height: 640px;
}

.field-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.is-expanded {
  flex-grow: 1;
}
.filter-dropdown {
  margin-left: 10px;
}
/* Prevent the background color on selection */
.filter-dropdown .dropdown-item {
  color: black !important;
}

/*Remove background color on hover or active state */
.filter-dropdown .dropdown-item.is-active,
.filter-dropdown .dropdown-item.is-hovered {
  background-color: transparent !important;
}
.checkbox-left {
  display: inline-block;
  padding-left: 10px;
  margin-right: 10px;
}
.filter-dropdown .dropdown-item .b-checkbox {
  display: inline-flex;
  align-items: center;
}

/* Archived toggle button style */
.archived-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: calc(0.5em - 1px) 1em;
  background-color: #f5f5f5;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 2.5em;
  box-sizing: border-box;
}

.archived-btn:hover {
  background-color: #eeeeee;
  border-color: #b5b5b5;
}

.archived-btn.is-active {
  background-color: #e8e8e8;
  border-color: #b5b5b5;
}

.archived-btn .b-switch {
  margin: 0 !important;
}

.archived-btn-text {
  font-size: 16px;
  font-weight: 400;
  color: #363636;
  white-space: nowrap;
}

.archived-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.35rem;
  background-color: #7a7a7a;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  border-radius: 10px;
  letter-spacing: 0.3px;
}

.archived-toggle-wrapper {
  margin-left: 0.5rem;
}

.toggle-label {
  font-weight: 600;
  font-size: 1.1rem;
  color: #1f2937;
}
.tag {
  border-radius: 9999px;
  font-size: 0.85rem;
  padding: 0.25em 0.75em;
}

/* Archive icon in status column */
.archive-icon-container {
  width: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.archive-status-icon {
  font-size: 1rem;
}

@media (max-width: 768px) {
  .archive-icon-container {
    width: 1.5rem;
  }

  .archive-status-icon {
    font-size: 1.25rem !important;
  }

  .archive-status-icon :deep(.mdi) {
    font-size: 1.25rem !important;
  }
}

.action-buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

/* Mobile action icons - hidden on desktop and tablet, only show in card view */
.mobile-action-icons {
  display: none;
}

@media (max-width: 768px) {
  .mobile-action-icons {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-left: 0.5rem;
  }

  .mobile-action-icons .icon {
    cursor: pointer;
  }
}

/* Hide Buefy table mobile sort dropdown */
@media (max-width: 1023px) {
  :deep(.table-mobile-sort) {
    display: none !important;
  }
}

/* Hide only the "select all" checkbox in header in card view, keep row checkboxes */
@media (max-width: 768px) {
  :deep(.b-table .table th.checkbox-cell) {
    display: none !important;
  }

  /* Search box on top, Archived and Search Fields on same row below */
  .field-body {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .field-body .is-expanded {
    flex: 1 1 100%;
    order: 1;
  }

  .field-body .is-expanded :deep(input) {
    width: 100%;
  }

  /* Archived switch and Search Fields button on same row */
  .field-body .archived-toggle-wrapper {
    order: 2;
    flex-shrink: 0;
    margin-left: 0;
  }

  .field-body .archived-btn {
    padding: 0.3em 0.5em;
    gap: 0.25rem;
    height: 2.25em;
  }

  .field-body .archived-btn-text {
    font-size: 13px;
  }

  .field-body .archived-count {
    min-width: 1.1rem;
    height: 1.1rem;
    font-size: 0.65rem;
    padding: 0 0.25rem;
  }

  .field-body .filter-dropdown {
    order: 3;
    flex-shrink: 0;
    margin-left: auto;
  }

  .field-body .filter-dropdown :deep(.button) {
    padding: 0.4em 0.75em;
    height: 2.35em;
    font-size: 14px;
  }

  /* Hide the count number on mobile */
  .filter-count {
    display: none;
  }

  /* Shorten bulk action button text on mobile */
  .bulk-action-text {
    display: none;
  }

  /* Make bulk action buttons full width on mobile */
  .bulk-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    width: 100%;
  }

  .bulk-actions :deep(.button) {
    flex: 1 1 calc(50% - 0.25rem);
    min-width: calc(50% - 0.25rem);
    margin-left: 0 !important;
  }

  /* When there's only one button, make it full width */
  .bulk-actions :deep(.button:only-child) {
    flex: 1 1 100%;
    min-width: 100%;
  }

  /* Move bulk actions below search section on mobile */
  .inquiry-table-section {
    display: flex;
    flex-direction: column;
  }

  .search-section {
    order: 1;
  }

  .bulk-actions {
    order: 2;
  }

  :deep(.b-table) {
    order: 3;
  }

  .pagination-container {
    order: 4;
  }
}
.alert-pill {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  display: inline-block;
}

.loader-container {
  display: flex;
  flex-direction: column;
  align-items: center;   
  justify-content: center;
  padding: 3rem 0;
}

.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #6f42c1;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  text-align: center;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

</style>
